import { Controller } from "@hotwired/stimulus";

// Controller for making an input accept only numbers
export default class extends Controller {
  connect() {
    this.isFocused = false;
    // Force dot as decimal separator regardless of browser locale
    this.element.setAttribute("lang", "en-US");
    this.element.addEventListener("keydown", this._onKeyDown.bind(this));
    this.element.addEventListener("input", this._onInput.bind(this));
    this.element.addEventListener("focus", this._onFocus.bind(this));
    this.element.addEventListener("blur", this._onBlur.bind(this));
  }

  get isIntegerOnly() {
    return this.element.dataset.integer !== undefined;
  }

  _onFocus(event) {
    // Only select all if the field wasn't already focused
    if (!this.isFocused) {
      event.target.select();
      this.isFocused = true;
    }
  }

  _onKeyDown(event) {
    // Allow modifier key combinations (Ctrl+C, Ctrl+V, Cmd+A, etc.)
    if (event.ctrlKey || event.metaKey) {
      return;
    }

    // Allow special keys for navigation and editing
    const allowedKeys = [
      "Backspace",
      "Delete",
      "Tab",
      "Escape",
      "Enter",
      "ArrowLeft",
      "ArrowRight",
      "ArrowUp",
      "ArrowDown",
      "Home",
      "End",
    ];

    if (allowedKeys.includes(event.key)) {
      return;
    }

    // Allow decimal point unless in integer-only mode
    if (event.key === "." && !this.isIntegerOnly) {
      return;
    }

    // Allow numeric digits (0-9)
    if (event.key >= "0" && event.key <= "9") {
      return;
    }

    // Prevent all other characters
    event.preventDefault();
  }

  _onInput(event) {
    const value = event.target.value;
    const cursorPosition = event.target.selectionStart;

    // Remove any non-digit characters, optionally excluding decimal point
    const pattern = this.isIntegerOnly ? /[^0-9]/g : /[^0-9.]/g;
    // Normalize commas to dots so pasted locale-specific decimals stay as dots
    const normalizedValue = value.replace(/,/g, ".");
    const sanitizedValue = normalizedValue.replace(pattern, "");

    // Only update if the value changed
    if (value !== sanitizedValue) {
      event.target.value = sanitizedValue;
      // Restore cursor position, adjusting for removed characters
      const removedChars = value.length - sanitizedValue.length;
      event.target.setSelectionRange(
        cursorPosition - removedChars,
        cursorPosition - removedChars,
      );
    }
  }

  _onBlur(event) {
    // Reset the focus state when field loses focus
    this.isFocused = false;

    let value = event.target.value;

    if (value === "" || value === ".") {
      return;
    }

    if (this.isIntegerOnly) {
      // Remove all leading zeros for integers
      value = value.replace(/^0+/, "") || "0";
    } else {
      // For decimals: keep one leading zero if value starts with a decimal point
      // Otherwise remove leading zeros (but keep at least one digit before decimal)
      if (value.startsWith(".")) {
        value = "0" + value;
      } else {
        value = value.replace(/^0+(?=\d)/, "");
      }
    }

    event.target.value = value;
  }
}
